/**
 * Payee Options Service
 * Fetches active drivers and partners for payout payee selection
 */

import api from "@/shared/api";
import { logError, logInfo } from "@/shared/utils/logger.js";

/**
 * Get all payee options (drivers + partners) for selection
 * @returns {Promise<Array>} Array of payee options with { id, label, type, meta }
 */
export const getPayeeOptions = async () => {
  try {
    const [driversRes, partnersRes] = await Promise.all([
      api.get("/drivers", { params: { isActive: true } }),
      api.get("/partners", { params: { isActive: true } }),
    ]);

    // Handle response format (data may be nested or direct)
    const driversPayload = driversRes?.data || driversRes;
    const partnersPayload = partnersRes?.data || partnersRes;

    const drivers = Array.isArray(driversPayload?.data)
      ? driversPayload.data
      : Array.isArray(driversPayload)
      ? driversPayload
      : [];

    const partners = Array.isArray(partnersPayload?.data)
      ? partnersPayload.data
      : Array.isArray(partnersPayload)
      ? partnersPayload
      : [];

    const options = [
      ...drivers.map((d) => {
        const payeeId = d.id || d._id || "";
        return {
          id: payeeId,
          label: `${d.name || "Driver"} (${payeeId})`,
          type: "DRIVER",
          meta: d,
        };
      }),
      ...partners.map((p) => {
        const payeeId = p.id || p._id || "";
        return {
          id: payeeId,
          label: `${p.name || "Partner"} (${payeeId})`,
          type: "PARTNER",
          meta: p,
        };
      }),
    ]
      .filter((opt) => opt.id) // Filter out entries without valid IDs
      .sort((a, b) => a.label.localeCompare(b.label));

    logInfo("Payee options loaded", {
      driversCount: drivers.length,
      partnersCount: partners.length,
      totalOptions: options.length,
    });

    return options;
  } catch (error) {
    logError("Failed to load payee options", {
      error: error.message,
      status: error.response?.status,
    });
    throw error;
  }
};

/**
 * Get job-scoped payee options (providers who have invoice items on this job)
 * @param {string} jobId - Job ID
 * @returns {Promise<Array>} Array of payee options with { id, label, type, meta }
 */
export const getJobPayeeOptions = async (jobId) => {
  try {
    if (!jobId) {
      throw new Error("jobId is required");
    }

    const response = await api.get(`/payouts/jobs/${jobId}/payee-options`);
    const payload = response?.data || response;

    const options = Array.isArray(payload?.data?.options)
      ? payload.data.options
      : Array.isArray(payload?.options)
      ? payload.options
      : [];

    logInfo("Job payee options loaded", {
      jobId,
      optionsCount: options.length,
    });

    return options;
  } catch (error) {
    logError("Failed to load job payee options", {
      jobId,
      error: error.message,
      status: error.response?.status,
    });
    throw error;
  }
};
